import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';

import {
  userLessonSettingsApi,
  type UserLessonSettings,
} from '@/lib/api-client';
import { queryKeys } from '@/config/query-client';
import {
  DEFAULT_DEPTH,
  DEFAULT_FLOW,
  type FlowPreset,
} from '@/config/lesson-settings-options';
import type { LessonDepth } from '@/types/api';

/**
 * The account-level default lesson flow + depth (`/users/me/lesson-settings`).
 *
 * Four surfaces read this — the onboarding ProfileStep, Settings → Learning
 * Style, Settings → Lesson Flow, and the LearningInterface prompt — and every
 * one of them used to inline `useQuery({ queryKey: ['user-lesson-settings'] })`
 * plus its own `setQueryData` after a write. That was the only hand-rolled
 * query key left in the app, and it meant a fifth caller could trivially miss
 * the cache write and go stale.
 */
export function useUserLessonSettings(
  options?: Pick<UseQueryOptions<UserLessonSettings>, 'staleTime' | 'retry'>
) {
  return useQuery<UserLessonSettings>({
    queryKey: queryKeys.user.lessonSettings(),
    queryFn: () => userLessonSettingsApi.get(),
    staleTime: 60_000,
    ...options,
  });
}

export function useUpdateUserLessonSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Partial<UserLessonSettings>) =>
      userLessonSettingsApi.update(payload),
    onSuccess: result =>
      queryClient.setQueryData(queryKeys.user.lessonSettings(), result),
  });
}

/**
 * The saved flow + depth, falling back to the product defaults.
 *
 * `flow` is narrowed to the three built-in presets: the stored value may also
 * be `'custom'` (built in Settings → Lesson Flow), which onboarding's
 * three-tile grid cannot represent. A custom flow shows as the default rather
 * than as no selection at all.
 */
export function resolveLessonPreferences(settings?: UserLessonSettings): {
  depth: LessonDepth;
  flow: FlowPreset;
} {
  const stored = settings?.default_lesson_settings;
  const flow = stored?.flow;
  return {
    depth: stored?.options?.depth ?? DEFAULT_DEPTH,
    flow: flow && flow !== 'custom' ? flow : DEFAULT_FLOW,
  };
}
